import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquareHeart, Star } from "lucide-react";

const Head = ({ icon: Icon, title, desc, checked, onChange, testid }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center text-emerald-700 dark:text-emerald-200 shrink-0"><Icon className="w-5 h-5" /></div>
    <div className="flex-1 min-w-0"><div className="font-semibold">{title}</div><div className="text-sm text-muted-foreground mt-0.5">{desc}</div></div>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

export const GreetingCard = ({ s, update }) => {
  const [text, setText] = useState(s.greeting_text);
  const [menuText, setMenuText] = useState(s.greeting_menu_text);
  const [deps, setDeps] = useState([]);
  useEffect(() => { api.get("/departments").then(({ data }) => setDeps(data.filter((d) => d.active !== false))).catch(() => {}); }, []);
  const chosen = s.greeting_departments || [];
  const visible = chosen.length ? deps.filter((d) => chosen.includes(d.name)) : deps;
  const toggleDep = (name) => {
    const base = chosen.length ? chosen : deps.map((d) => d.name);
    const next = base.includes(name) ? base.filter((n) => n !== name) : [...base, name];
    update({ greeting_departments: next.length === deps.length ? [] : next });
  };

  return (
    <Card data-testid="greeting-card"><CardContent className="p-5 space-y-4">
      <Head icon={MessageSquareHeart} title="Mensagem de saudação" testid="settings-greeting-toggle" checked={s.greeting_enabled} onChange={(v) => update({ greeting_enabled: v })}
            desc="Enviada automaticamente quando um cliente inicia (ou retoma) uma conversa. Use {empresa} e {cliente}." />
      <div>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid="settings-greeting-text" />
        <Button size="sm" variant="outline" className="mt-2" onClick={() => update({ greeting_text: text })} data-testid="settings-greeting-save">Salvar saudação</Button>
      </div>
      <div className="flex items-center justify-between gap-4 border-t border-border pt-4">
        <div className="text-sm"><div className="font-medium">Oferecer menu de setores</div><div className="text-xs text-muted-foreground">O cliente responde com o número e a conversa vai para a fila do setor escolhido.</div></div>
        <Switch checked={!!s.greeting_menu} onCheckedChange={(v) => update({ greeting_menu: v })} data-testid="settings-greeting-menu-toggle" />
      </div>
      {s.greeting_menu && (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Texto antes da lista</Label>
            <div className="flex gap-2 mt-1">
              <Textarea value={menuText} onChange={(e) => setMenuText(e.target.value)} rows={1} data-testid="settings-greeting-menu-text" />
              <Button size="sm" variant="outline" onClick={() => update({ greeting_menu_text: menuText })} data-testid="settings-greeting-menu-save">Salvar</Button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Setores exibidos no menu</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {deps.map((d) => {
                const on = !chosen.length || chosen.includes(d.name);
                return (
                  <button key={d.id || d.name} type="button" onClick={() => toggleDep(d.name)} data-testid={`greeting-dep-${d.name}`}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-emerald-600 text-white border-emerald-600" : "border-border text-muted-foreground hover:bg-muted"}`}>{d.name}</button>
                );
              })}
            </div>
          </div>
          <div className="rounded-lg bg-muted/50 p-3 text-xs whitespace-pre-line font-mono" data-testid="greeting-preview">
            {`${text.replaceAll("{empresa}", "Sua Empresa").replaceAll("{cliente}", "Maria")}\n\n${menuText}\n${visible.map((d, i) => `${i + 1} - ${d.name}`).join("\n")}`}
          </div>
        </div>
      )}
    </CardContent></Card>
  );
};

export const RatingCard = ({ s, update }) => {
  const [text, setText] = useState(s.rating_text);
  const [thanks, setThanks] = useState(s.rating_thanks);
  return (
    <Card data-testid="rating-card"><CardContent className="p-5 space-y-4">
      <Head icon={Star} title="Avaliação do atendimento" testid="settings-rating-toggle" checked={s.rating_enabled} onChange={(v) => update({ rating_enabled: v })}
            desc="Ao finalizar a conversa, o cliente recebe um pedido de nota de 1 a 5. As notas ficam em Relatórios › Avaliações. Use {atendente}." />
      {s.rating_enabled && (
        <div className="space-y-3">
          <div><Label className="text-xs">Pergunta</Label><Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} className="mt-1" data-testid="settings-rating-text" /></div>
          <div><Label className="text-xs">Agradecimento após a nota</Label><Textarea value={thanks} onChange={(e) => setThanks(e.target.value)} rows={1} className="mt-1" data-testid="settings-rating-thanks" /></div>
          <Button size="sm" variant="outline" onClick={() => update({ rating_text: text, rating_thanks: thanks })} data-testid="settings-rating-save">Salvar textos</Button>
        </div>
      )}
    </CardContent></Card>
  );
};
